import * as React from "react";
import {
    TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography,
    IconButton,
    Tooltip,
    Box,
    Chip
} from "@mui/material";
import { DocumentTestOutcomeChip } from "./utils/DocumentTestOutcomeChip";
import { If } from "../../../app/If";
import { Localized } from "@fluent/react";
import { LpField } from "../../../app/components/LpField";
import { DocumentContentActions } from "../documentsApi";
import { EditIconButton } from "../../../app/components/iconButtons/EditIconButton";
import CommentIcon from '@mui/icons-material/Comment';
import { DeleteIconButton } from "../../../app/components/iconButtons/DeleteIconButton";
import { useAppSelector } from "../../../app/hooks";
import { docDeviationsSelectors, docTestsSelectors, selectDocument, showDocComments } from "../documentSlice";
import { AllCommentsCounter } from "../CommentsCounter";
import ChecklistIcon from '@mui/icons-material/Checklist';
import LaunchIcon from '@mui/icons-material/Launch';
import { formatDate, impossible, parseISODate } from "../../../app/Utils";
import { DocumentTest } from "./documentTestsApi";

type DocumentTestsFlatTableProps = {
    onOpenTest?: (documentTestId: number) => void;
    onOpenTestResult?: (documentTestId: number) => void;
    onDeleteTest?: (documentTestId: number) => void;
    sortByOrder?: boolean;
    actions: DocumentContentActions[],
    sectionId: string
};

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}


// ===== komponent =====
export const DocumentTestsFlatTable: React.FC<DocumentTestsFlatTableProps> = ({ actions, onOpenTest, onOpenTestResult, onDeleteTest, sectionId }) => {
    const showComments = useAppSelector(state => showDocComments(state, sectionId))

    const rows = useAppSelector(state => docTestsSelectors.selectAll(state).filter(t => t.sectionId === sectionId))

    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()

    const editable = hasAction(actions, "Edit") && header.documentKind !== 'REPORT'

    return (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell width={56}><b>#</b></TableCell>
                        <TableCell><b>Test</b></TableCell>
                        <TableCell width={160}>
                            <b>
                                <Localized id="description">
                                    <span>_description</span>
                                </Localized>
                            </b>
                        </TableCell>
                        <TableCell width={140}>
                            <b>
                                <Localized id="test-result">
                                    <span>_test-result</span>
                                </Localized>
                            </b>
                        </TableCell>
                        <TableCell>Data testu</TableCell>
                        <TableCell>
                            <Localized id='deviations'>Odchylenia</Localized>
                        </TableCell>
                        <TableCell width={140} align="right" />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                <Localized id="document-no-tests">
                                    <span>_Brak testów.</span>
                                </Localized>
                            </TableCell>
                        </TableRow>
                    )}

                    {rows.map((row, index) => {
                        const name = row.testSpecification?.name || "—";
                        const description = row.testSpecification?.description || "—";

                        return (
                            <TableRow hover key={`${row.id}-${row.id}`}>
                                <TableCell>
                                    <LpField value={index + 1} />
                                </TableCell>
                                <TableCell sx={{ minWidth: 260 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                                    {row.sectionName && (
                                        <Typography variant="caption" color="text.secondary">
                                            Sekcja: {row.sectionName}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ minWidth: 260 }}>
                                    <div dangerouslySetInnerHTML={{ __html: description }}></div>
                                </TableCell>

                                <TableCell>
                                    <DocumentTestOutcomeChip outcome={row.lastRunOutcome} />
                                </TableCell>

                                <TableCell>{row.lastRunExecutedAt ? formatDate((parseISODate(row.lastRunExecutedAt))) : ''}</TableCell>

                                <TableCell>
                                    <DeviationsCell test={row} />
                                </TableCell>


                                <TableCell align="right">
                                    <If condition={editable}>
                                        <>
                                            <EditIconButton
                                                tooltip={<Localized id='edit'>_edit</Localized>}
                                                onClick={() => onOpenTest?.(row.id)}
                                            />
                                            <DeleteIconButton
                                                tooltip={<Localized id='delete'>_delete</Localized>}
                                                onClick={() => onDeleteTest?.(row.id)}
                                            />
                                        </>
                                    </If>
                                    <If condition={!editable}>
                                        <Tooltip title={<Localized id="open">Otwórz</Localized>}>
                                            <IconButton size='small' onClick={() => onOpenTest?.(row.id)}>
                                                <LaunchIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </If>
                                    <If condition={header.documentKind === 'REPORT'}>
                                        <Tooltip title={<Localized id="results">Wyniki</Localized>}>
                                            <IconButton size='small' onClick={() => onOpenTestResult?.(row.id)}>
                                                <ChecklistIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </If>
                                    <If condition={showComments}>
                                        <AllCommentsCounter sectionId={row.sectionId ?? ''} contextGuid={row.guid}>
                                            <Tooltip title={<Localized id='comments'>Komentarze</Localized>}>
                                                <IconButton
                                                    sx={{
                                                        color: "action.active",
                                                    }}
                                                    onClick={() => onOpenTest?.(row.id)}
                                                >
                                                    <CommentIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </AllCommentsCounter>
                                    </If>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const DeviationsCell = (props: { test: DocumentTest }) => {
    const { test } = props

    // non-closed only
    const devs = useAppSelector(state => docDeviationsSelectors.selectAll(state).filter(d => d.status !== 'CLOSED' && d.tests.findIndex(t => t.documentTestId === props.test.id) >= 0))

    const critical = devs.filter(d => d.classification === 'CRITICAL').length
    const major = devs.filter(d => d.classification === 'MAJOR').length
    const minor = devs.filter(d => d.classification === 'MINOR').length

    let retest: boolean = false
    if (test.lastRunExecutedAt) {
        const latestRun = parseISODate(test.lastRunExecutedAt)
        const devsWithRetest = devs.filter(d => d.status !== 'CLOSED' && d.retestDescription === 'YES' && d.correctorSignatureDate && parseISODate(d.correctorSignatureDate) > latestRun)
        if (devsWithRetest.length > 0) {
            retest = true
        }
    }

    return <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: .5,
        // justifyContent: 'center',
        alignItems: 'center',
    }}>
        {/* {devs.length > 0 && <Chip size='small' variant='outlined' label={`Wszystkie: ${devs.length}`} />} */}
        {critical > 0 && <Tooltip title={<Localized id='deviation-classification-critical'>Krytyczne</Localized>}>
            <Chip size='small' color='error' label={`${critical}`} />
        </Tooltip>}
        {major > 0 && <Tooltip title={<Localized id='deviation-classification-major'>Ważne</Localized>}>
            <Chip size='small' color='warning' label={`${major}`} />
        </Tooltip>}
        {minor > 0 && <Tooltip title={<Localized id='deviation-classification-minor'>Drobne</Localized>}>
            <Chip size='small' color='info' label={`${minor}`} />
        </Tooltip>}
        {retest && <Chip size='small' variant='outlined' color='error' label={`Ponowny test`} />}
    </Box>
}
